import axios from 'axios';

import ErrorHandler from '../components/ErrorHandler';
import { scanningConfig } from '../Config';
import { BoxRes } from '../types/Box';
import { Logger, LoggerListItem } from '../types/Logger';
import { ScanBoxCommon, ScanRes } from '../types/Scan';
import { getToken } from './AuthHelpers';
import { formatDateTime } from './FormattingHelpers';

type StorageLimitKey = 'min-logger-count' | 'max-logger-count';
export type LimitType = 'min' | 'max';

export const getLoggerCountLimitKey = (limit: LimitType, scanId?: string, boxId?: string): string | null => {
    const limitKey: StorageLimitKey = limit === 'min' ? 'min-logger-count' : 'max-logger-count';
    if (boxId && scanId) return `${scanId}-${boxId}-${limitKey}`;
    else if (scanId) return `${scanId}-${limitKey}`;
    return null;
};

export const setLoggerLimitToStore = (limit: LimitType, value?: number, scanId?: string, boxId?: string): void => {
    const loggerCountLimitScanKey = getLoggerCountLimitKey(limit, scanId, boxId);
    if (value && loggerCountLimitScanKey) {
        localStorage.setItem(loggerCountLimitScanKey, value.toString());
    } else if (loggerCountLimitScanKey) {
        // Remove from storage if the logger count limit is cleared
        localStorage.removeItem(loggerCountLimitScanKey);
    }
};

export const getLoggerLimitFromStore = (limit: LimitType, scanId?: string, boxId?: string): string | null => {
    const loggerCountLimitScanKey = getLoggerCountLimitKey(limit, scanId, boxId);
    return loggerCountLimitScanKey ? localStorage.getItem(loggerCountLimitScanKey) : null;
};

export const findBoxInScan = (scan: ScanRes, boxId: string): BoxRes | undefined => {
    return Array.isArray(scan.boxes) ? scan.boxes.find((elem: BoxRes) => elem.id === parseInt(boxId, 10)) : undefined;
};

export const updateListElementWithNewCheckData = (
    checkResponses: Array<Logger>,
    loggerList: Array<LoggerListItem>
): Array<LoggerListItem> => {
    const scannedItems = [...loggerList];
    let isListUpdated = false;

    checkResponses.forEach((checkResponse) => {
        const pendingItemIndex = loggerList.findIndex(
            (elem: LoggerListItem) => elem.loggerId === checkResponse.loggerId
        );

        if (pendingItemIndex > -1) {
            scannedItems[pendingItemIndex] = checkResponse;
            isListUpdated = true;
        }
    });

    return isListUpdated ? scannedItems : loggerList;
};

// documentType: Type of document that should be fetched. F.x. pdf or excel. Used in api url
// fileExtension: File extension. Used for creating file name and fetching mime type
// scanInfo: Information about scan. Used to fetch the correct documentation and constructing file name
export const downloadScanDocument = (
    documentType: string,
    fileExtension: string,
    scan: ScanBoxCommon | undefined | null
): void => {
    if (scan) {
        const url = `${scanningConfig.apiPath}/scans/${scan.id}/${documentType}`;
        const fileName = `scanId-${scan.id}_${formatDateTime(scan.createdTime, 'YYYYMMDD_HHmm')}.${fileExtension}`;
        downloadFile(url, fileExtensionToMime(fileExtension), fileName);
    }
};

// Add more extension-mime cases if needed
export const fileExtensionToMime = (fileExtension: string): string => {
    const base = 'application/';
    switch (fileExtension) {
        case 'xlsx':
            return base + 'vnd.ms-excel';
        case 'pdf':
            return base + 'pdf';
        default:
            return '';
    }
};

export const downloadFile = (url: string, mime: string, fileName: string) => {
    const arrayBufferOptions: any = {
        headers: { Authorization: `Bearer ${getToken()}` },
        method: 'GET',
        responseType: 'blob',
        mime
    };
    axios
        .create()
        .get(url, arrayBufferOptions)
        .then((response: any) => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: mime }));

            const a = document.createElement('a');
            document.body.appendChild(a);

            a.href = url;
            a.download = fileName;
            a.click();

            // For Firefox it is necessary to delay revoking the ObjectURL
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 100);

            document.body.removeChild(a);
        })
        .catch(async (err) => {
            try {
                const errMessage = JSON.parse(await err?.response?.data?.text());
                ErrorHandler({ data: errMessage });
            } catch (error) {
                ErrorHandler(null, "Couldn't download file");
            }
        });
};

export const getScanStatusClassName = (status: 'COMPLETE' | 'CANCELED' | 'IN_PROGRESS'): string => {
    if (status === 'COMPLETE') {
        return 'success';
    }
    if (status === 'CANCELED') {
        return 'default';
    }
    return '';
};

export const getCompleteBtnDataTipMessage = (
    scanBelongsToUserLocation: boolean,
    failedOnSubmit: boolean,
    limitCountErr: boolean,
    listLength: number
) => {
    if (!scanBelongsToUserLocation) {
        return 'Not available on a remote location';
    } else if (failedOnSubmit) {
        return 'Remove failed loggers';
    } else if (limitCountErr) {
        return 'Total logger count not within min/max limit';
    } else if (listLength === 0) {
        return 'No loggers have been scanned';
    }
    return '';
};
